import React, { useMemo } from 'react'
import styled from 'styled-components'
import ReactECharts from 'echarts-for-react'
import type { TFunction } from 'i18next'

interface FARUtilizationChartProps {
  indiceType: string // SP, SPD, or STD
  currentUtilization?: number
  maxUtilization?: number
  availableSpace?: number
  t: TFunction
}

const ChartContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
`

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
  text-align: center;
`

const ChartSubtitle = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 20px 0;
  text-align: center;
`

const MetricsRow = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

const MetricBox = styled.div`
  text-align: center;
`

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const FARUtilizationChart: React.FC<FARUtilizationChartProps> = ({
  indiceType,
  currentUtilization = 0,
  maxUtilization = 1,
  availableSpace = 0,
  t,
}) => {
  const utilizationPercent =
    maxUtilization > 0
      ? ((currentUtilization / maxUtilization) * 100).toFixed(1)
      : '0'

  const option = useMemo(() => {
    const percentage = parseFloat(utilizationPercent)

    // Determine color based on utilization
    let color = '#10b981' // Green for low utilization
    if (percentage > 90) {
      color = '#ef4444' // Red for very high
    } else if (percentage > 75) {
      color = '#f59e0b' // Orange for high
    } else if (percentage > 50) {
      color = '#eab308' // Yellow for medium
    }

    return {
      series: [
        {
          type: 'gauge',
          min: 0,
          max: 100,
          splitNumber: 4,
          radius: '80%',
          center: ['50%', '60%'],
          startAngle: 180,
          endAngle: 0,

          // Main axis line
          axisLine: {
            lineStyle: {
              width: 30,
              color: [
                [0.5, '#10b981'], // Green: 0-50%
                [0.75, '#eab308'], // Yellow: 50-75%
                [0.9, '#f59e0b'], // Orange: 75-90%
                [1, '#ef4444'], // Red: 90-100%
              ],
            },
          },

          // Pointer
          pointer: {
            width: 5,
            length: '70%',
            itemStyle: {
              color: color,
            },
          },

          // Tick marks
          axisTick: {
            distance: -30,
            length: 6,
            lineStyle: {
              color: '#fff',
              width: 1,
            },
          },

          // Split lines
          splitLine: {
            distance: -30,
            length: 12,
            lineStyle: {
              color: '#fff',
              width: 2,
            },
          },

          // Labels
          axisLabel: {
            distance: 10,
            color: '#64748b',
            fontSize: 11,
            formatter: '{value}%',
          },

          // Center detail
          detail: {
            valueAnimation: true,
            formatter: '{value}%',
            fontSize: 28,
            fontWeight: 'bold',
            color: color,
            offsetCenter: [0, '85%'],
          },

          // Title
          title: {
            offsetCenter: [0, '105%'],
            fontSize: 13,
            color: '#64748b',
          },

          // Data
          data: [
            {
              value: parseFloat(utilizationPercent),
              name: t('elevationCharts.far.used'),
            },
          ],

          // Anchor
          anchor: {
            show: true,
            showAbove: true,
            size: 15,
            itemStyle: {
              color: color,
              borderWidth: 3,
              borderColor: '#fff',
            },
          },
        },
      ],

      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicOut',
    }
  }, [utilizationPercent, t])

  return (
    <ChartContainer>
      <ChartTitle>{t('elevationCharts.far.title', { indiceType })}</ChartTitle>
      <ChartSubtitle>{t('elevationCharts.far.subtitle')}</ChartSubtitle>

      <ReactECharts
        option={option}
        style={{ height: '250px', width: '100%' }}
        opts={{ renderer: 'svg' }}
      />

      <MetricsRow>
        <MetricBox>
          <MetricValue>{currentUtilization.toFixed(2)}</MetricValue>
          <MetricLabel>{t('elevationCharts.far.current')}</MetricLabel>
        </MetricBox>

        <MetricBox>
          <MetricValue>{maxUtilization.toFixed(2)}</MetricValue>
          <MetricLabel>{t('elevationCharts.far.maximum')}</MetricLabel>
        </MetricBox>

        <MetricBox>
          <MetricValue>
            {availableSpace > 0 ? (
              <span style={{ color: '#10b981' }}>
                {Math.round(availableSpace)} m²
              </span>
            ) : (
              <span style={{ color: '#ef4444' }}>0 m²</span>
            )}
          </MetricValue>
          <MetricLabel>{t('elevationCharts.far.available')}</MetricLabel>
        </MetricBox>
      </MetricsRow>
    </ChartContainer>
  )
}
